import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ReactComponent as NavLogo } from '../assets/navbar/isolation-mode.svg';
import { ReactComponent as LangIcon } from '../assets/navbar/lang-icon.svg';
import { ReactComponent as ProfileIcon } from '../assets/navbar/profile-icon.svg';
import { ReactComponent as ArrowRightIcon } from '../assets/navbar/arrow-right.svg';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import LoginModal from '../pages/Login';

const NAV_LINK_ITEMS = [
  { id: 'about', labelKey: 'website.nav.about' },
  { id: 'speakers', labelKey: 'website.nav.speakers' },
  { id: 'schedule', labelKey: 'website.nav.schedule' },
  { id: 'opportunities', labelKey: 'website.nav.opportunities' },
  { id: 'sponsors', labelKey: 'website.nav.sponsors' },
];

const Navigation = () => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('home');
  const [isLoginOpen, setIsLoginOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { t, changeLanguage, language, isRTL } = useLanguage();
  const { isAuthenticated, user } = useAuth();
  const role = typeof user?.role === 'string' ? user.role.toLowerCase().trim() : '';
  const isDashboardRole = ['admin', 'super_admin', 'superadmin', 'organizer'].includes(role);
  const isApplicant = role === 'applicant';
  const applicantNeedsSetup = isApplicant && !Boolean(user?.profile_completed);
  const authTargetPath = isAuthenticated
    ? (isDashboardRole ? '/dashboard' : (applicantNeedsSetup ? '/dashboard/applicant-setup' : '/profile'))
    : '#';
  const logoTargetPath = isAuthenticated
    ? (isDashboardRole ? '/dashboard' : (applicantNeedsSetup ? '/dashboard/applicant-setup' : '/profile'))
    : '/';

  // External contact URL
  const contactUrl = "https://investoman.om/investor-info-form";

  const toggleLanguage = () => {
    changeLanguage(language === 'en' ? 'ar' : 'en');
  };

  const scrollToSection = useCallback((sectionId, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const element = document.getElementById(sectionId);
    if (sectionId === 'home') {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    } else if (element) {
      const navbar = document.querySelector('nav');
      const navbarHeight = navbar ? navbar.offsetHeight : 0;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      const offsetPosition = elementPosition - navbarHeight - 20;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  }, []);

  const closeMobileMenu = useCallback(() => {
    setMobileMenuOpen(false);
  }, []);

  useEffect(() => {
    if (!mobileMenuOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') closeMobileMenu();
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [mobileMenuOpen, closeMobileMenu]);

  const activeSectionRef = useRef('home');

  useEffect(() => {
    const sectionIds = [
      'home',
      'about',
      'why-oman',
      'speakers',
      'schedule',
      'face-to-face-meetings',
      'why-oman-infrastructure',
      'opportunities',
      'sponsors',
    ];
    const sectionElements = sectionIds
      .map((id) => document.getElementById(id))
      .filter(Boolean);

    if (!sectionElements.length) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        let bestEntry = null;
        for (const entry of entries) {
          if (!entry.isIntersecting) {
            continue;
          }
          if (!bestEntry || entry.intersectionRatio > bestEntry.intersectionRatio) {
            bestEntry = entry;
          }
        }

        if (!bestEntry?.target?.id) {
          return;
        }

        const nextSection = bestEntry.target.id;
        if (activeSectionRef.current !== nextSection) {
          activeSectionRef.current = nextSection;
          setActiveSection(nextSection);
        }
      },
      {
        root: null,
        threshold: [0.2, 0.4, 0.6, 0.8],
        rootMargin: '-120px 0px -45% 0px',
      }
    );

    sectionElements.forEach((element) => observer.observe(element));

    return () => {
      observer.disconnect();
    };
  }, []);

  // Animation variants
  const navVariants = {
    hidden: { opacity: 0, y: -20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        staggerChildren: 0.08,
        delayChildren: 0.1
      }
    }
  };

  return (
    <div
      className="fixed top-0 left-0 right-0 z-50 flex justify-center px-3 sm:px-4 md:px-6 lg:px-8 pt-3 md:pt-4"
      style={{ transform: 'translateZ(0)' }}
    >
      <motion.nav
        className="relative w-full max-w-[1344px] shadow-[0px_4px_20.5px_4px_rgba(0,0,0,0.05)] rounded-[71px] overflow-hidden"
        style={{
          transform: 'translateZ(0)',
          backfaceVisibility: 'hidden',
          outline: '0.5px solid rgba(255, 255, 255, 0.60)',
          outlineOffset: '-0.5px',
        }}
        initial="hidden"
        animate="visible"
        variants={navVariants}
        dir="ltr"
      >
        {/* Figma glass gradient: linear-gradient(135deg, rgba(18,20,58,0.49), rgba(0,153,206,0.91)) */}
        <div
          className="absolute inset-0"
          aria-hidden="true"
          style={{
            background: 'linear-gradient(135deg, rgba(18, 20, 58, 0.49) 0%, rgba(0, 153, 206, 0.91) 100%)',
            backdropFilter: 'blur(10px)',
          }}
        />
        <div className="relative flex items-center justify-between py-2.5 md:py-3 px-3 sm:px-4 md:px-5 min-h-[62px] md:min-h-[72px] gap-3">

          {/* LEFT: Logo — Figma: 152×48 */}
          <motion.div
            className="flex-shrink-0 z-20"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, type: "spring" }}
            whileHover={{ scale: 1.05 }}
          >
            <Link
              to={logoTargetPath}
              className="flex items-center justify-center"
              onClick={(e) => {
                if (!isAuthenticated) {
                  scrollToSection('home', e);
                }
              }}
            >
              <NavLogo className="h-8 sm:h-9 md:h-10 w-auto transition-all duration-300" />
            </Link>
          </motion.div>

          {/* CENTER: Links only on larger screens (no hamburger on small) */}
          <motion.ul className="hidden lg:flex items-center gap-[6px] whitespace-nowrap px-2 flex-1 min-w-0 overflow-x-auto scrollbar-hide" variants={navVariants}>
            {NAV_LINK_ITEMS.map((item) => (
              <motion.li key={item.id} className="relative" whileHover={{ y: -2 }} whileTap={{ scale: 0.95 }}>
                <motion.a
                  href={`#${item.id}`}
                  onClick={(e) => scrollToSection(item.id, e)}
                  className={`px-2.5 py-2 transition-all duration-300 rounded-[37px] block relative whitespace-nowrap font-semibold text-[13px] ${
                    activeSection === item.id ? 'text-white' : 'text-[#EDEAF3] hover:text-white'
                  }`}
                  style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                >
                  {activeSection === item.id && (
                    <motion.div
                      layoutId="navPill"
                      className="absolute inset-0 bg-white/10 rounded-[37px] -z-10 border border-white/20"
                      style={{ willChange: 'transform', backfaceVisibility: 'hidden' }}
                      transition={{ type: "spring", stiffness: 400, damping: 30 }}
                    />
                  )}
                  {t(item.labelKey)}
                </motion.a>
              </motion.li>
            ))}
          </motion.ul>

          {/* RIGHT: Priority actions */}
          <div className="flex items-center gap-[10px]">
            {/* Language — desktop only (mobile: in hamburger menu) */}
            <motion.button
              type="button"
              onClick={toggleLanguage}
              className="hidden lg:inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/10 px-3 py-1.5 text-white shadow-[0_1px_2px_rgba(0,0,0,0.08)] backdrop-blur-md transition-all hover:border-white/45 hover:bg-white/18"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              aria-label={language === 'en' ? 'Switch to Arabic' : 'Switch to English'}
              title={language === 'en' ? 'Switch to Arabic' : 'Switch to English'}
            >
              <LangIcon className="h-[18px] w-[18px] shrink-0 opacity-95" aria-hidden="true" />
              <span
                className="text-[12px] font-bold tracking-[0.08em]"
                style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
              >
                {language === 'en' ? 'EN' : 'AR'}
              </span>
            </motion.button>

            {/* Profile */}
            <Link
              to={authTargetPath}
              onClick={(e) => {
                if (!isAuthenticated) {
                  e.preventDefault();
                  setIsLoginOpen(true);
                }
              }}
              className="inline-flex h-[38px] w-[38px] items-center justify-center rounded-[10px] bg-transparent text-white transition-all hover:bg-white/10"
              aria-label={isAuthenticated ? (isDashboardRole ? 'Open dashboard' : 'Open profile') : 'Sign in'}
              title={isAuthenticated ? (isDashboardRole ? 'Dashboard' : 'My Profile') : 'Login'}
            >
              <ProfileIcon className="h-5 w-5" />
            </Link>

            {/* CTA — desktop only (mobile: in hamburger menu) */}
            <motion.a
              href={contactUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden lg:inline-flex h-[38px] px-[12px] sm:px-[14px] rounded-[50px] items-center gap-[6px] text-white font-semibold text-[12px] sm:text-[13px] leading-[18px] whitespace-nowrap"
              style={{
                background: 'linear-gradient(90deg, rgba(66, 42, 126, 0.70) 0%, rgba(66, 42, 126, 0.49) 100%)',
                boxShadow: '0px 1px 2px rgba(16, 24, 40, 0.05)',
                fontFamily: "'Plus Jakarta Sans', sans-serif",
              }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.96 }}
            >
              <span className="hidden sm:inline">{t('website.nav.ctaInvest')}</span>
              <span className="sm:hidden">Invest</span>
              <ArrowRightIcon className="h-3 w-[6px]" aria-hidden="true" />
            </motion.a>

            {/* Mobile: open full menu (desktop nav links + language + CTA live here too) */}
            <button
              type="button"
              className="lg:hidden inline-flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-[10px] text-white transition-all hover:bg-white/10"
              onClick={() => setMobileMenuOpen(true)}
              aria-expanded={mobileMenuOpen}
              aria-controls="mobile-nav-panel"
              aria-label="Open navigation menu"
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                <path d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
        </div>

      </motion.nav>

      {/* Mobile menu: portaled to body so it stacks above page content (main is a sibling with its own layers) */}
      {mobileMenuOpen &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            className="fixed inset-0 z-[10000] flex flex-col gap-3 p-3 pt-14 sm:pt-16 lg:hidden"
            role="dialog"
            aria-modal="true"
            aria-label="Navigation menu"
            id="mobile-nav-panel"
            dir={isRTL ? 'rtl' : 'ltr'}
            style={{ isolation: 'isolate' }}
          >
          <button
            type="button"
            className="absolute inset-0 bg-black/50 backdrop-blur-[2px]"
            aria-label="Close menu"
            onClick={closeMobileMenu}
          />
          <div className="relative z-10 mx-auto flex w-full max-w-[1344px] flex-col gap-3 overflow-y-auto overscroll-contain pb-6">
            {/* Top pill: logo + profile + close */}
            <div
              className="flex shrink-0 items-center justify-between rounded-full px-4 py-3 shadow-[0px_8px_32px_rgba(0,0,0,0.2)]"
              style={{
                background: 'linear-gradient(90deg, #0d9488 0%, #2563eb 100%)',
              }}
            >
              <Link
                to={logoTargetPath}
                className="flex items-center justify-center"
                onClick={(e) => {
                  closeMobileMenu();
                  if (!isAuthenticated) {
                    scrollToSection('home', e);
                  }
                }}
              >
                <NavLogo className="h-8 w-auto" />
              </Link>
              <div className="flex items-center gap-2">
                <Link
                  to={authTargetPath}
                  onClick={(e) => {
                    closeMobileMenu();
                    if (!isAuthenticated) {
                      e.preventDefault();
                      setIsLoginOpen(true);
                    }
                  }}
                  className="inline-flex h-10 w-10 items-center justify-center rounded-[10px] text-white transition-all hover:bg-white/15"
                  aria-label={isAuthenticated ? (isDashboardRole ? 'Open dashboard' : 'Open profile') : 'Sign in'}
                >
                  <ProfileIcon className="h-5 w-5" />
                </Link>
                <button
                  type="button"
                  className="inline-flex h-10 w-10 items-center justify-center rounded-[10px] text-white transition-all hover:bg-white/15"
                  onClick={closeMobileMenu}
                  aria-label="Close menu"
                >
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                    <path d="M18 6L6 18M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Main panel: links + language + CTA */}
            <div
              className="flex flex-col gap-6 rounded-[28px] p-6 shadow-[0px_12px_40px_rgba(0,0,0,0.25)]"
              style={{
                background: 'linear-gradient(145deg, #0d9488 0%, #134e4a 45%, #312e81 100%)',
              }}
            >
              <nav className="flex flex-col gap-1" aria-label="Main">
                {NAV_LINK_ITEMS.map((item) => (
                  <a
                    key={item.id}
                    href={`#${item.id}`}
                    className={`rounded-xl px-2 py-3 text-base font-semibold text-white transition-colors hover:bg-white/10 ${
                      activeSection === item.id ? 'bg-white/10' : ''
                    }`}
                    style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                    onClick={(e) => {
                      scrollToSection(item.id, e);
                      closeMobileMenu();
                    }}
                  >
                    {t(item.labelKey)}
                  </a>
                ))}
              </nav>

              <button
                type="button"
                className="flex w-full items-center justify-between gap-3 rounded-2xl border border-white/25 bg-white/10 px-4 py-3 text-left text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.12)] transition-colors hover:bg-white/15"
                onClick={() => toggleLanguage()}
              >
                <span className="inline-flex min-w-0 items-center gap-3">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/15 ring-1 ring-white/20">
                    <LangIcon className="h-5 w-5" aria-hidden="true" />
                  </span>
                  <span className="flex min-w-0 flex-col gap-0.5">
                    <span
                      className="text-[11px] font-medium uppercase tracking-wider text-white/65"
                      style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                    >
                      {t('website.nav.languageToggle')}
                    </span>
                    <span
                      className="truncate text-[15px] font-semibold leading-tight"
                      style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                    >
                      {language === 'en' ? t('website.nav.languageEn') : t('website.nav.languageAr')}
                    </span>
                  </span>
                </span>
                <span
                  className="shrink-0 rounded-full bg-white/20 px-2.5 py-1 text-[11px] font-bold tracking-wider text-white"
                  style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                >
                  {language === 'en' ? 'EN' : 'AR'}
                </span>
              </button>

              <motion.a
                href={contactUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex w-full items-center justify-center gap-2 rounded-[50px] px-5 py-3.5 text-white font-semibold text-[15px]"
                style={{
                  background: 'linear-gradient(90deg, rgba(66, 42, 126, 0.95) 0%, rgba(49, 32, 96, 0.95) 100%)',
                  boxShadow: '0px 4px 14px rgba(0, 0, 0, 0.2)',
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                }}
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.98 }}
                onClick={closeMobileMenu}
              >
                {t('website.nav.ctaInvest')}
                <ArrowRightIcon className="h-3 w-[6px]" aria-hidden="true" />
              </motion.a>
            </div>
          </div>
        </div>,
          document.body
        )}

      <LoginModal
        isOpen={isLoginOpen && !isAuthenticated}
        onClose={() => setIsLoginOpen(false)}
        onSuccess={(loggedInUser) => {
          const nextRole = typeof loggedInUser?.role === 'string' ? loggedInUser.role.toLowerCase().trim() : '';
          const isDashboardRole = ['admin', 'super_admin', 'superadmin', 'organizer'].includes(nextRole);
          const isApplicant = nextRole === 'applicant';
          if (isDashboardRole) {
            navigate('/dashboard');
            return;
          }
          if (isApplicant) {
            navigate('/dashboard/applicant-setup');
            return;
          }
          navigate('/profile');
        }}
      />
    </div>
  );
};

export default Navigation;